import { create } from 'zustand'
import type {
  PlaybackState,
  PlaybackItemRole,
  PlaylistItem,
  UserSettings,
  CloudProvider,
  SortOrder,
  BumperRule,
} from '../types'

// ─── Playback Store ───────────────────────────────────────────────────────────

interface PlaybackStore extends PlaybackState {
  playlist: PlaylistItem[]
  filteredPlaylist: PlaylistItem[]

  setPlaylist: (items: PlaylistItem[]) => void
  setCurrentVideo: (index: number, role?: PlaybackItemRole) => void
  setCurrentVideoById: (videoId: string, role?: PlaybackItemRole) => void
  setStatus: (status: PlaybackState['status']) => void
  setPosition: (position: number) => void
  setDuration: (duration: number) => void
  setVolume: (volume: number) => void
  setMuted: (muted: boolean) => void
  setFullscreen: (fullscreen: boolean) => void
  setSidebarOpen: (open: boolean) => void
  setTheaterMode: (open: boolean) => void
  setSortOrder: (order: SortOrder) => void
  setSearchQuery: (query: string) => void
  setError: (message: string | null) => void
  clearPlaybackDiagnostics: () => void
  addToQueue: (videoId: string) => void
  addToQueueNext: (videoId: string) => void
  addManyToQueue: (videoIds: string[]) => void
  setQueue: (videoIds: string[]) => void
  setActiveSavedPlaylist: (playlistId: string | null, revision?: number, resumePosition?: number | null) => void
  clearPendingResumePosition: () => void
  setBumperRule: (rule: BumperRule) => void
  resetBumperSequence: (resuming?: boolean) => void
  skipBumper: () => void
  removeQueuedVideo: (queueIndex: number) => void
  moveQueuedVideo: (queueIndex: number, direction: -1 | 1) => void
  playQueuedVideo: (queueIndex: number) => void
  clearQueue: () => void

  nextVideo: () => void
  previousVideo: () => void
  toggleTheaterMode: () => void
}

export const usePlaybackStore = create<PlaybackStore>((set, get) => ({
  // State
  currentVideoId: null,
  currentIndex: -1,
  currentPlaybackRole: 'video',
  pendingVideoIdAfterBumper: null,
  completeAfterBumper: false,
  videosSinceBumper: 0,
  position: 0,
  duration: 0,
  status: 'idle',
  volume: 1,
  muted: false,
  isFullscreen: false,
  isSidebarOpen: true,
  isTheaterMode: false,
  queueIds: [],
  activeSavedPlaylistId: null,
  savedPlaylistRevision: 0,
  pendingResumePosition: null,
  bumperEnabled: false,
  bumperVideoId: null,
  bumperFrequency: 3,
  bumperPlacement: 'after_every_n',
  bumperSkipAllowed: true,
  bumperVolume: 1,
  sortOrder: 'name',
  searchQuery: '',
  errorMessage: null,
  playbackDiagnostics: [],
  playlist: [],
  filteredPlaylist: [],

  setPlaylist: (items) => {
    const { searchQuery } = get()
    const filtered = searchQuery
      ? items.filter((i) =>
          i.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : items
    const validIds = new Set(items.map((item) => item.id))
    const queueIds = get().queueIds.filter((id) => validIds.has(id))
    set({ playlist: items, filteredPlaylist: filtered, queueIds })
  },

  setCurrentVideo: (index, role = 'video') => {
    const { playlist } = get()
    const item = playlist[index]
    if (!item) return
    set({
      currentIndex: index,
      currentVideoId: item.id,
      currentPlaybackRole: role,
      position: 0,
      status: 'loading',
      errorMessage: null,
    })
  },

  setCurrentVideoById: (videoId, role = 'video') => {
    const index = get().playlist.findIndex((item) => item.id === videoId)
    if (index < 0) return
    get().setCurrentVideo(index, role)
  },

  setStatus: (status) => set({ status }),
  setPosition: (position) => set({ position }),
  setDuration: (duration) => set({ duration }),
  setVolume: (volume) => set({ volume }),
  setMuted: (muted) => set({ muted }),
  setFullscreen: (isFullscreen) => set({ isFullscreen }),
  setSidebarOpen: (isSidebarOpen) => set({ isSidebarOpen }),
  setTheaterMode: (isTheaterMode) => set({ isTheaterMode }),

  setSortOrder: (sortOrder) => set({ sortOrder }),

  setSearchQuery: (searchQuery) => {
    const { playlist } = get()
    const filtered = searchQuery
      ? playlist.filter((i) =>
          i.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : playlist
    set({ searchQuery, filteredPlaylist: filtered })
  },

  setError: (errorMessage) =>
    set((state) => {
      if (!errorMessage) {
        return { errorMessage, status: 'idle' }
      }

      const currentVideo = state.playlist.find((item) => item.id === state.currentVideoId)
      return {
        errorMessage,
        status: 'error',
        playbackDiagnostics: [
          {
            id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            videoId: state.currentVideoId,
            videoName: currentVideo?.name ?? null,
            message: errorMessage,
            createdAt: new Date().toISOString(),
          },
          ...state.playbackDiagnostics,
        ].slice(0, 10),
      }
    }),

  clearPlaybackDiagnostics: () => set({ playbackDiagnostics: [] }),

  addToQueue: (videoId) => {
    const exists = get().playlist.some((item) => item.id === videoId)
    if (!exists) return
    set((state) => ({ queueIds: [...state.queueIds, videoId] }))
  },

  addToQueueNext: (videoId) => {
    const exists = get().playlist.some((item) => item.id === videoId)
    if (!exists) return
    set((state) => ({ queueIds: [videoId, ...state.queueIds] }))
  },

  addManyToQueue: (videoIds) => {
    const validIds = new Set(get().playlist.map((item) => item.id))
    const nextIds = videoIds.filter((videoId) => validIds.has(videoId))
    if (nextIds.length === 0) return
    set((state) => ({ queueIds: [...state.queueIds, ...nextIds] }))
  },

  setQueue: (videoIds) => {
    const validIds = new Set(get().playlist.map((item) => item.id))
    set({ queueIds: videoIds.filter((videoId) => validIds.has(videoId)) })
  },

  setActiveSavedPlaylist: (activeSavedPlaylistId, savedPlaylistRevision = 0, pendingResumePosition = null) => set({
    activeSavedPlaylistId,
    savedPlaylistRevision,
    pendingResumePosition,
  }),
  clearPendingResumePosition: () => set({ pendingResumePosition: null }),

  setBumperRule: (rule) => {
    const safeFrequency = Math.min(20, Math.max(1, Math.floor(rule.frequency || 3)))
    set({
      bumperEnabled: rule.enabled,
      bumperVideoId: rule.videoId,
      bumperFrequency: safeFrequency,
      bumperPlacement: rule.placement,
      bumperSkipAllowed: rule.skipAllowed,
      bumperVolume: Math.min(1, Math.max(0, rule.volume)),
    })
  },

  resetBumperSequence: (resuming = false) => set({
    currentPlaybackRole: 'video',
    pendingVideoIdAfterBumper: null,
    completeAfterBumper: false,
    videosSinceBumper: resuming ? 1 : 0,
  }),

  skipBumper: () => {
    const state = get()
    if (state.currentPlaybackRole === 'bumper' && state.bumperSkipAllowed) state.nextVideo()
  },

  removeQueuedVideo: (queueIndex) => {
    set((state) => ({
      queueIds: state.queueIds.filter((_, index) => index !== queueIndex),
    }))
  },

  moveQueuedVideo: (queueIndex, direction) => {
    set((state) => {
      const targetIndex = queueIndex + direction
      if (targetIndex < 0 || targetIndex >= state.queueIds.length) {
        return {}
      }

      const queueIds = [...state.queueIds]
      const [item] = queueIds.splice(queueIndex, 1)
      queueIds.splice(targetIndex, 0, item)
      return { queueIds }
    })
  },

  playQueuedVideo: (queueIndex) => {
    const { queueIds } = get()
    const videoId = queueIds[queueIndex]
    if (!videoId) return

    get().removeQueuedVideo(queueIndex)
    get().setCurrentVideoById(videoId)
  },

  clearQueue: () => set({ queueIds: [] }),

  nextVideo: () => {
    const {
      currentIndex,
      currentVideoId,
      playlist,
      queueIds,
      currentPlaybackRole,
      pendingVideoIdAfterBumper,
      videosSinceBumper,
      bumperEnabled,
      bumperVideoId,
      bumperFrequency,
      bumperPlacement,
      activeSavedPlaylistId,
      completeAfterBumper,
    } = get()

    if (currentPlaybackRole === 'bumper') {
      if (completeAfterBumper) {
        set({
          currentVideoId: null, currentIndex: -1, currentPlaybackRole: 'video',
          pendingVideoIdAfterBumper: null, completeAfterBumper: false, status: 'idle',
        })
        return
      }
      set({ pendingVideoIdAfterBumper: null, currentPlaybackRole: 'video' })
      if (pendingVideoIdAfterBumper) {
        if (bumperPlacement === 'before_first') set({ videosSinceBumper: 1 })
        get().setCurrentVideoById(pendingVideoIdAfterBumper, 'video')
        return
      }
    }

    if (playlist.length === 0) return
    const next = (currentIndex + 1) % playlist.length
    const [nextQueuedId, ...remainingQueueIds] = queueIds
    const nextTargetId = nextQueuedId ?? playlist[next]?.id
    if (!nextTargetId) return
    const nextVideosSinceBumper = videosSinceBumper + 1
    const bumperIndex = bumperVideoId
      ? playlist.findIndex((item) => item.id === bumperVideoId)
      : -1
    const shouldPlayBumper =
      bumperEnabled &&
      bumperVideoId &&
      bumperIndex >= 0 &&
      currentPlaybackRole !== 'bumper' &&
      get().currentVideoId !== bumperVideoId &&
      nextTargetId !== bumperVideoId &&
      ((bumperPlacement === 'before_first' && videosSinceBumper === 0) ||
        (bumperPlacement === 'after_every_n' && nextVideosSinceBumper >= bumperFrequency))

    const shouldPlayEndBumper = Boolean(
      activeSavedPlaylistId && queueIds.length === 0 && currentVideoId &&
      bumperEnabled && bumperVideoId && bumperIndex >= 0 && bumperPlacement === 'playlist_end',
    )

    if (shouldPlayEndBumper) {
      set({ completeAfterBumper: true, pendingVideoIdAfterBumper: null })
      get().setCurrentVideo(bumperIndex, 'bumper')
      return
    }

    if (activeSavedPlaylistId && queueIds.length === 0 && currentVideoId) {
      set({
        currentVideoId: null,
        currentIndex: -1,
        status: 'idle',
        position: 0,
        duration: 0,
        pendingVideoIdAfterBumper: null,
      })
      return
    }

    if (shouldPlayBumper) {
      set({
        queueIds: nextQueuedId ? remainingQueueIds : queueIds,
        pendingVideoIdAfterBumper: nextTargetId,
        videosSinceBumper: 0,
      })
      get().setCurrentVideo(bumperIndex, 'bumper')
      return
    }

    if (nextQueuedId) {
      set({
        queueIds: remainingQueueIds,
        videosSinceBumper: nextVideosSinceBumper,
      })
      get().setCurrentVideoById(nextQueuedId)
      return
    }

    set({ videosSinceBumper: nextVideosSinceBumper })
    get().setCurrentVideo(next)
  },

  previousVideo: () => {
    const { currentIndex, playlist } = get()
    if (playlist.length === 0) return
    const prev = currentIndex <= 0 ? playlist.length - 1 : currentIndex - 1
    get().setCurrentVideo(prev)
  },

  toggleTheaterMode: () => {
    set((state) => ({ isTheaterMode: !state.isTheaterMode }))
  },
}))

// ─── Settings Store ───────────────────────────────────────────────────────────

interface SettingsStore {
  settings: UserSettings | null
  isLoaded: boolean
  setSettings: (s: UserSettings) => void
  updateSettings: (patch: Partial<UserSettings>) => void
}

export const useSettingsStore = create<SettingsStore>((set) => ({
  settings: null,
  isLoaded: false,
  setSettings: (settings) => set({ settings, isLoaded: true }),
  updateSettings: (patch) =>
    set((state) => ({
      settings: state.settings ? { ...state.settings, ...patch } : null,
    })),
}))

// ─── Auth Store ───────────────────────────────────────────────────────────────

interface ProviderAuth {
  connected: boolean
  expiresAt: number | null
  email: string | null
}

interface AuthStore {
  google: ProviderAuth
  onedrive: ProviderAuth
  setProviderAuth: (provider: CloudProvider, state: ProviderAuth) => void
}

export const useAuthStore = create<AuthStore>((set) => ({
  google: { connected: false, expiresAt: null, email: null },
  onedrive: { connected: false, expiresAt: null, email: null },
  setProviderAuth: (provider, state) => set({ [provider]: state }),
}))
